import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface InventoryAlert {
  product_naam: string;
  product_id: string;
  voorraad: number;
  magazijn: string;
  moq: number;
  status: "critical" | "low" | "warning";
}

interface InventoryStats {
  totalProducts: number;
  lowStockItems: number;
  criticalItems: number;
  outOfStockItems: number;
}

export const useRealtimeInventory = (refreshInterval: number) => {
  // Inventory alerts
  const { data: inventoryAlerts, refetch: refetchAlerts } = useQuery({
    queryKey: ["inventory-alerts"],
    queryFn: async (): Promise<InventoryAlert[]> => {
      const { data, error } = await supabase
        .from("voorraad_live2")
        .select("product_naam, product_id, voorraad, magazijn, moq")
        .not("voorraad", "is", null)
        .order("voorraad", { ascending: true })
        .limit(100);

      if (error) throw error;

      return data
        .filter(item => item.voorraad !== null && item.voorraad !== undefined)
        .map(item => {
          const moq = item.moq || 10; // Default MOQ if not set
          const voorraad = item.voorraad || 0;
          
          let status: "critical" | "low" | "warning";
          if (voorraad === 0) {
            status = "critical";
          } else if (voorraad <= moq * 0.5) {
            status = "critical";
          } else if (voorraad <= moq) {
            status = "low";
          } else {
            status = "warning";
          }

          return {
            product_naam: item.product_naam || "Onbekend product",
            product_id: item.product_id,
            voorraad,
            magazijn: item.magazijn || "Onbekend",
            moq,
            status
          };
        })
        .filter(item => item.voorraad <= item.moq * 1.5) // Only show items below 1.5x MOQ
        .slice(0, 50); // Limit results
    },
    refetchInterval: refreshInterval * 1000,
  });

  // Inventory statistics
  const { data: inventoryStats, refetch: refetchStats } = useQuery({
    queryKey: ["inventory-stats"],
    queryFn: async (): Promise<InventoryStats> => {
      const { data, error } = await supabase
        .from("voorraad_live2")
        .select("voorraad, moq")
        .not("voorraad", "is", null);

      if (error) throw error;

      const stats = data.reduce((acc, item) => {
        const voorraad = item.voorraad || 0;
        const moq = item.moq || 10;
        
        acc.totalProducts++;
        
        if (voorraad === 0) {
          acc.outOfStockItems++;
          acc.criticalItems++;
        } else if (voorraad <= moq * 0.5) {
          acc.criticalItems++;
        } else if (voorraad <= moq) {
          acc.lowStockItems++;
        }
        
        return acc;
      }, {
        totalProducts: 0,
        lowStockItems: 0,
        criticalItems: 0,
        outOfStockItems: 0
      });

      return stats;
    },
    refetchInterval: refreshInterval * 1000,
  });

  // Real-time subscription for inventory changes
  useEffect(() => {
    const channel = supabase
      .channel('inventory-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'voorraad_live2'
        },
        () => {
          refetchAlerts();
          refetchStats();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetchAlerts, refetchStats]);

  return {
    inventoryAlerts: inventoryAlerts || [],
    inventoryStats: inventoryStats || {
      totalProducts: 0,
      lowStockItems: 0,
      criticalItems: 0,
      outOfStockItems: 0
    },
    isLoading: !inventoryAlerts || !inventoryStats
  };
};